import type { CreateProjectPayload, ProjectStatus, SpsSurvey } from '../types'
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useDssToast } from '@/shared/composables/messages/dss-toast.composable'
import { useDayJs } from '@/shared/composables/use-dayjs'
import { useProjects } from '../composables'
import { LanguagesDropdownMode } from '../types'

interface Language {
  id: number
  code: string
  name: string
}

interface WizardStep {
  value: number
  description: string | undefined
}

interface SurveyFormData {
  surveyName: string
  surveyDescription: string
  startDate: Date
  endDate: Date
  industry: string
  collectIP: boolean
  defaultLanguage: string
  showLanguagesDropdown: LanguagesDropdownMode
  showLanguagesNativeScript: boolean
  showCaptcha: boolean
  showProgressBar: boolean
  includeSifQuestions: boolean
  includePsmQuestions: boolean
  status: ProjectStatus
  languages: string[]
}

interface FormErrors {
  name: string
  startDate: string
  endDate: string
  industry: string
}

interface State {
  mode: 'creation' | 'edition'
  loading: boolean
  saveLoading: boolean
  currentStep: number
  wizardSteps: WizardStep[]
  form: SurveyFormData
  formErrors: FormErrors
  showPreviewDrawer: boolean
  created: boolean
  createdProjectId: string
  createdSurveyEditorId: string
  surveyJSON: SpsSurvey | null
  surveyUrl: string
  previewUrl: string
  returnBtnLabel: string
  proceedBtnLabel: string
  publishBtnLabel: string
  saveBtnLabel: string
  previewLabel: string
  publishSurveyDialog: boolean
  previewDrawer: boolean
}

export const useProjectSetupStore = defineStore('projectSetupStore', () => {
  const { t } = useI18n()
  const { success, error } = useDssToast()
  const useProjectsComposable = useProjects()
  const dayJs = useDayJs()
  const router = useRouter()

  const state = reactive<State>({
    mode: 'creation',
    loading: false,
    saveLoading: false,
    currentStep: 1,
    wizardSteps: [
      { value: 1, description: t('sps.projectDetails') },
      { value: 2, description: t('sps.projectsDetails.surveyEditor') },
      { value: 3, description: t('sps.surveyLanguages') },
    ],
    form: {
      surveyName: '',
      surveyDescription: '',
      startDate: new Date(),
      endDate: new Date(dayJs.addDays(14)),
      industry: undefined,
      collectIP: false,
      defaultLanguage: 'en',
      showLanguagesDropdown: LanguagesDropdownMode.FirstPage,
      showLanguagesNativeScript: true,
      showCaptcha: false,
      showProgressBar: false,
      includeSifQuestions: false,
      includePsmQuestions: false,
      status: 'draft',
      languages: [],
    },
    formErrors: {
      name: '',
      startDate: '',
      endDate: '',
      industry: '',
    },
    showPreviewDrawer: false,
    created: false,
    createdProjectId: '',
    createdSurveyEditorId: '',
    surveyJSON: null,
    surveyUrl: '',
    previewUrl: '',
    returnBtnLabel: t('sps.form.cancel'),
    proceedBtnLabel: t('sps.form.next'),
    publishBtnLabel: t('sps.surveyEditor.publishProject'),
    saveBtnLabel: t('sps.form.saveChanges'),
    previewLabel: t('sps.form.previewSurvey'),
    publishSurveyDialog: false,
    previewDrawer: false,
  })

  // Closed projects open in read-only mode: the survey and details are locked
  // and only the industry benchmark can be changed.
  const isReadOnly = computed(() => state.form.status === 'closed')

  // The survey name is only editable while the project has not been published,
  // so it stays in sync with the survey already sent to respondents.
  const canEditSurveyName = computed(() => state.mode === 'creation' || state.form.status === 'draft')

  async function setProjectToEdit(id: string): Promise<void> {
    try {
      state.mode = 'edition'
      const project = await useProjectsComposable.fetchProjectDetails(id)

      Object.keys(state.form).forEach((key) => {
        if (key in project) {
          state.form[key] = project[key]
        }
      })

      state.form.startDate = new Date(state.form.startDate)
      state.form.endDate = new Date(state.form.endDate)
      state.created = true
      state.createdProjectId = project.id
      state.createdSurveyEditorId = project.survey?.previewUuid ?? ''
      state.surveyUrl = project.surveyUrl
      state.previewUrl = project.previewUrl

      if (project.status === 'active') {
        state.publishBtnLabel = t('sps.surveyEditor.saveAndPublish')
      }
      else {
        state.publishBtnLabel = t('sps.surveyEditor.publishProject')
      }
    }
    catch (error) {
      console.error(error)
    }
  }

  async function next(): Promise<void> {
    state.loading = true

    if (state.currentStep === 1) {
      const canProceed = validateForm()
      if (!canProceed) {
        state.loading = false
        return
      }

      if (state.created) {
        await updateProject()
        state.currentStep++
      }
      else {
        await createProject()
      }
    }
    else if (state.currentStep === 2) {
      const saved = await saveSurveyJSON()
      if (!saved) {
        state.loading = false
        return
      }
      state.currentStep++
      state.proceedBtnLabel = t('sps.surveyEditor.publishProject')
    }
    else if (state.currentStep === 3) {
      state.publishSurveyDialog = true
    }

    state.loading = false
  }

  async function publishEdition(): Promise<void> {
    if (state.mode === 'edition' && state.form.status === 'active') {
      await saveAndPublish()
    }
    else {
      state.publishSurveyDialog = true
    }
  }

  async function saveAndPublish(): Promise<void> {
    try {
      await updateProject()
      // The survey rides along with the publish: the editor tab only persists
      // its JSON when it is left, so publishing from it would otherwise put out
      // the version the backend already had rather than the one on screen.
      await useProjectsComposable.activateProject(state.createdProjectId, state.surveyJSON)

      success(
        t('sps.publishSurveyDialog.publishSuccessTitle'),
        t('sps.publishSurveyDialog.publishSuccessMessage'),
        5000,
      )
      await router.push({ name: 'sps-home' })
    }
    catch (error_: unknown) {
      error(
        t('sps.publishSurveyDialog.publishErrorTitle'),
        (error_ as { response?: { data?: { message?: string } } })?.response?.data?.message
        || t('sps.publishSurveyDialog.publishErrorMessage'),
        5000,
      )
    }
  }

  async function previous(): Promise<void> {
    state.loading = true

    if (state.currentStep === 1) {
      resetWizard()
      await router.push({ name: 'sps-home' })
    }
    else if (state.currentStep === 2) {
      state.returnBtnLabel = t('sps.form.cancel')
      state.currentStep--
    }
    else if (state.currentStep === 3) {
      state.proceedBtnLabel = t('sps.form.next')
      state.currentStep--
    }

    state.loading = false
  }

  async function saveDraft(): Promise<void> {
    state.saveLoading = true
    if (state.mode === 'edition' && state.currentStep === 2) {
      await saveSurveyJSON()
    }
    else if (!state.createdProjectId) {
      // The project has not been persisted yet (creation flow), so it must be
      // created with a POST instead of issuing a PUT against a non-existent id.
      if (validateForm())
        await createProject({ advanceStep: false })
    }
    else {
      await updateProject()
    }
    state.saveLoading = false
  }

  function validateForm(): boolean {
    const hasErrors = Object.values(state.formErrors).some(error => !!error)
    const requiredFieldsFilled = state.form.surveyName && state.form.industry !== undefined
    const isValid = !hasErrors && requiredFieldsFilled

    if (!isValid) {
      updateFormErrors('name', state.form.surveyName ? '' : t('sps.form.errors.surveyNameRequired'))
      updateFormErrors('industry', state.form.industry ? '' : t('sps.form.errors.surveyIndustryRequired'))
    }

    return isValid
  }

  function updateFormErrors(field: string, message: string): void {
    state.formErrors = { ...state.formErrors, [field]: message }
  }

  function setDrawer(): void {
    state.previewDrawer = true
  }

  function unsetDrawer(): void {
    state.previewDrawer = false
  }

  function forceStep(step: number): void {
    state.currentStep = step

    if (state.currentStep === 3) {
      state.proceedBtnLabel = t('sps.surveyEditor.publishProject')
      state.returnBtnLabel = t('sps.form.previous')
    }
    else if (state.currentStep === 2) {
      state.proceedBtnLabel = t('sps.form.next')
      state.returnBtnLabel = t('sps.form.previous')
    }
    else if (state.currentStep === 1) {
      state.proceedBtnLabel = t('sps.form.next')
      state.returnBtnLabel = t('sps.form.cancel')
    }
  }

  function resetWizard(): void {
    const resetedForm: SurveyFormData = {
      surveyName: '',
      surveyDescription: '',
      startDate: new Date(),
      endDate: new Date(dayJs.addDays(14)),
      industry: undefined,
      collectIP: false,
      defaultLanguage: 'en',
      showLanguagesDropdown: LanguagesDropdownMode.FirstPage,
      showLanguagesNativeScript: true,
      showCaptcha: false,
      showProgressBar: false,
      includeSifQuestions: false,
      includePsmQuestions: false,
      status: 'draft',
      languages: [],
    }

    state.formErrors = {
      name: '',
      startDate: '',
      endDate: '',
      industry: '',
    }

    state.currentStep = 1
    state.form = resetedForm
    state.createdProjectId = ''
    state.createdSurveyEditorId = ''
    state.showPreviewDrawer = false
    state.created = false
    state.mode = 'creation'
    state.surveyUrl = ''
    state.previewUrl = ''
    state.returnBtnLabel = t('sps.form.cancel')
    state.proceedBtnLabel = t('sps.form.next')
    state.publishBtnLabel = t('sps.surveyEditor.publishProject')
    state.saveBtnLabel = t('sps.form.saveChanges')
    state.previewLabel = t('sps.form.previewSurvey')
  }

  function fillLanguage(langs: Language[]) {
    state.form.languages = langs.map(l => l.code)
  }

  /**
   * The survey opens in the default language, so that language is the one a new
   * project offers until the author picks more on the languages step. While the
   * list is still only that seed it follows the default instead of keeping the
   * one picked before, so a project never carries a language nobody chose.
   *
   * Creation only: an existing project's languages were published and may
   * already have translations uploaded, so there the default is added to them
   * rather than replacing them.
   */
  watch(
    () => state.form.defaultLanguage,
    (code, previous) => {
      if (state.mode !== 'creation')
        return

      const seeded = state.form.languages.length === 0
        || (state.form.languages.length === 1 && state.form.languages[0] === previous)

      if (seeded)
        state.form.languages = [code]
    },
  )

  /**
   * The API rejects a default language the project does not offer, so it is put
   * back if the languages step dropped it. Nothing else is added.
   */
  function withDefaultLanguage(languages: string[]): string[] {
    return languages.includes(state.form.defaultLanguage)
      ? languages
      : [...languages, state.form.defaultLanguage]
  }

  async function createProject({ advanceStep = true }: { advanceStep?: boolean } = {}): Promise<void> {
    try {
      let payload: CreateProjectPayload

      if (state.form.endDate) {
        payload = {
          ...state.form,
          languages: withDefaultLanguage(state.form.languages),
          loginUser: 'teste',
        }
      }
      else {
        const { endDate, ...cleanForm } = state.form
        payload = {
          ...cleanForm,
          languages: withDefaultLanguage(state.form.languages),
          loginUser: 'teste',
        }
      }
      const created = await useProjectsComposable.createProject(payload)

      if (!created?.id) {
        error(
          t('sps.form.errors.createSurveyTitle'),
          t('sps.form.errors.createSurveyGeneric'),
          5000,
        )
        return
      }

      state.createdProjectId = created.id
      state.createdSurveyEditorId = created.survey?.previewUuid ?? ''
      state.surveyUrl = created.surveyUrl
      state.previewUrl = created.previewUrl

      success(
        t('sps.form.success.createSurveyTitle'),
        t('sps.form.success.createSurveyMessage'),
        5000,
      )
      state.created = true

      if (advanceStep) {
        state.currentStep++
        state.returnBtnLabel = t('sps.form.previous')
      }
    }
    catch (error_: unknown) {
      error(
        t('sps.form.errors.createSurveyTitle'),
        (error_ as { response?: { data?: { message?: string } } })?.response?.data?.message || t('sps.form.errors.createSurveyGeneric'),
        5000,
      )
    }
    finally {
      state.loading = false
    }
  }

  async function saveSurveyJSON(): Promise<boolean> {
    if (!state.createdProjectId || !state.surveyJSON)
      return true

    const ok = await useProjectsComposable.updateSurveyJSON(state.createdProjectId, state.surveyJSON)
    if (!ok) {
      error(
        t('sps.form.errors.createSurveyTitle'),
        t('sps.form.errors.createSurveyGeneric'),
        5000,
      )
    }
    return ok
  }

  async function updateProject(): Promise<void> {
    try {
      const payload = {
        surveyName: state.form.surveyName,
        surveyDescription: state.form.surveyDescription,
        status: state.form.status,
        startDate: state.form.startDate,
        endDate: state.form.endDate,
        industry: state.form.industry,
        collectIP: state.form.collectIP,
        defaultLanguage: state.form.defaultLanguage,
        showLanguagesDropdown: state.form.showLanguagesDropdown,
        showLanguagesNativeScript: state.form.showLanguagesNativeScript,
        showCaptcha: state.form.showCaptcha,
        showProgressBar: state.form.showProgressBar,
        includeSifQuestions: state.form.includeSifQuestions,
        includePsmQuestions: state.form.includePsmQuestions,
        languages: withDefaultLanguage(state.form.languages),
        loginUser: 'teste',
      }

      const created = await useProjectsComposable.updateProject(state.createdProjectId, payload)

      if (!created?.id) {
        error(
          t('sps.form.errors.createSurveyTitle'),
          t('sps.form.errors.createSurveyGeneric'),
          5000,
        )
        return
      }

      // force watch to trigger and reload surveyJson
      state.createdProjectId = ''
      state.createdProjectId = created.id
      state.createdSurveyEditorId = created.survey?.previewUuid ?? ''

      success(
        t('sps.form.success.updateSurveyTitle'),
        t('sps.form.success.updateSurveyMessage'),
        5000,
      )
      state.created = true
    }
    catch (error_: unknown) {
      error(
        t('sps.form.errors.createSurveyTitle'),
        (error_ as { response?: { data?: { message?: string } } })?.response?.data?.message || t('sps.form.errors.createSurveyGeneric'),
        5000,
      )
    }
    finally {
      state.loading = false
    }
  }

  function clearEndDate(): void {
    state.form.endDate = null
  }

  return {
    state,
    isReadOnly,
    canEditSurveyName,
    setProjectToEdit,
    setDrawer,
    unsetDrawer,
    forceStep,
    resetWizard,
    next,
    previous,
    saveDraft,
    saveSurveyJSON,
    updateFormErrors,
    fillLanguage,
    publishEdition,
    clearEndDate,
  }
})
